import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 10;

export default function DepositSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const paId = params.get("pa_id");
  const [state, setState] = useState({ phase: "checking", attempts: 0, data: null });
  const attemptsRef = useRef(0);

  const poll = useCallback(async () => {
    if (!sessionId) { setState({ phase: "error", attempts: 0, data: { msg: "Missing session_id" } }); return; }
    try {
      const r = await api.get(`/payments/status/${sessionId}`);
      if (r.data.payment_status === "paid") {
        setState({ phase: "paid", attempts: attemptsRef.current, data: r.data });
        return;
      }
      if (r.data.status === "expired") {
        setState({ phase: "expired", attempts: attemptsRef.current, data: r.data });
        return;
      }
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState({ phase: "timeout", attempts: attemptsRef.current, data: r.data });
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    } catch (e) {
      setState({ phase: "error", attempts: attemptsRef.current, data: { msg: e?.response?.data?.detail || "Status check failed" } });
    }
  }, [sessionId]);

  useEffect(() => { poll(); }, [poll]);

  return (
    <div data-testid="page-deposit-success" className="mx-auto max-w-xl px-4 py-20">
      <div className="border border-border p-10">
        {state.phase === "checking" && (
          <div className="text-center" data-testid="deposit-checking">
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin" />
            <h1 className="font-display text-3xl mt-6">Confirming payment…</h1>
            <p className="text-muted-foreground text-sm mt-2 font-mono-data">
              Attempt {state.attempts + 1} / {MAX_ATTEMPTS}
            </p>
          </div>
        )}
        {state.phase === "paid" && (
          <div className="text-center" data-testid="deposit-paid">
            <CheckCircle2 className="w-12 h-12 mx-auto text-primary" />
            <h1 className="font-display text-3xl mt-6">Deposit Received</h1>
            <p className="text-muted-foreground mt-2">
              {fmtMoney((state.data?.amount_total || 0) / 100)} confirmed. Your pre-approval is now <b>ACTIVE</b>.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-2">
              <Button asChild variant="outline" className="rounded-none uppercase text-xs tracking-wider">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/preapprovals/${paId}/contract.pdf`}
                   target="_blank" rel="noreferrer" data-testid="download-contract">
                  Download Contract PDF
                </a>
              </Button>
              <Button onClick={() => navigate("/dashboard")} className="rounded-none uppercase text-xs tracking-wider"
                data-testid="goto-dashboard">My Dashboard</Button>
            </div>
          </div>
        )}
        {state.phase === "expired" && (
          <div className="text-center" data-testid="deposit-expired">
            <XCircle className="w-12 h-12 mx-auto text-destructive" />
            <h1 className="font-display text-3xl mt-6">Session Expired</h1>
            <p className="text-muted-foreground mt-2">The checkout session expired. Try again.</p>
            <Button asChild className="mt-6 rounded-none uppercase text-xs tracking-wider">
              <Link to="/dashboard">Back to Dashboard</Link>
            </Button>
          </div>
        )}
        {state.phase === "timeout" && (
          <div className="text-center" data-testid="deposit-timeout">
            <Loader2 className="w-12 h-12 mx-auto text-amber-600" />
            <h1 className="font-display text-3xl mt-6">Still processing</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              Stripe is taking longer than usual. We'll update your dashboard automatically when payment clears.
            </p>
            <Button onClick={() => navigate("/dashboard")} className="mt-6 rounded-none uppercase text-xs tracking-wider">Go to Dashboard</Button>
          </div>
        )}
        {state.phase === "error" && (
          <div className="text-center" data-testid="deposit-error">
            <XCircle className="w-12 h-12 mx-auto text-destructive" />
            <h1 className="font-display text-3xl mt-6">Something went wrong</h1>
            <p className="text-muted-foreground mt-2 text-sm">{state.data?.msg || "Unknown error"}</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-6 rounded-none uppercase text-xs tracking-wider">Back</Button>
          </div>
        )}
      </div>
    </div>
  );
}
